import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIcon, ExternalLinkIcon, XIcon, CheckCircleIcon, SearchIcon, CheckIcon, HashIcon, EditIcon, BanIcon, ListIcon, FileTextIcon } from 'lucide-react';
import { useRental } from '../../contexts/RentalContext';
type KanbanColumn = 'pending' | 'verified' | 'called' | 'completed';
export function ProspectingListsKanban() {
  const navigate = useNavigate();
  const {
    prospectingLeads,
    updateProspectingLead
  } = useRental();
  const [selectedLead, setSelectedLead] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const today = new Date().toISOString().split('T')[0];
  // Call status options
  const callStatusOptions = [{
    value: 'de-apelat',
    label: 'De apelat',
    valid: true
  }, {
    value: 'nu-raspunde',
    label: 'Nu răspunde',
    valid: true
  }, {
    value: 'nu-colaboreaza',
    label: 'Nu colaborează',
    valid: true
  }, {
    value: 'comision-redus',
    label: 'Comision redus',
    valid: true
  }, {
    value: 'colaboreaza',
    label: 'Colaborează ✔️',
    valid: true
  }, {
    value: 'nr-invalid',
    label: 'Nr. invalid ⛔',
    valid: false
  }, {
    value: 'inchiriat',
    label: 'A închiriat deja ⛔',
    valid: false
  }, {
    value: 'exclusivitate-alta',
    label: 'Exclusivitate altă agenție ⛔',
    valid: false
  }, {
    value: 'de-vanzare',
    label: 'De vânzare ⛔',
    valid: false
  }, {
    value: 'coleg-clc',
    label: 'Coleg CLC ⛔',
    valid: false
  }, {
    value: 'anunt-invalid',
    label: 'Anunț invalid ⛔',
    valid: false
  }, {
    value: 'agentie',
    label: 'Agenție ⛔',
    valid: false
  }];
  const isInvalidStatus = (status: string) => {
    const statusOption = callStatusOptions.find(opt => opt.value === status);
    return statusOption && !statusOption.valid;
  };
  // Get today's leads from context
  const currentLeads = useMemo(() => {
    return prospectingLeads.filter((lead: any) => lead.listDate === today || !lead.listDate);
  }, [prospectingLeads, today]);
  // Organize leads into Kanban columns
  const kanbanColumns = useMemo(() => {
    const columns: Record<KanbanColumn, any[]> = {
      pending: [],
      verified: [],
      called: [],
      completed: []
    };
    currentLeads.forEach((lead: any) => {
      const leadId = lead.id || lead.prospectId;
      const leadData = {
        ...lead,
        prospectId: leadId
      };
      if (leadData.propertyId || leadData.callStatus === 'colaboreaza') {
        columns.completed.push(leadData);
      } else if (leadData.callCompleted) {
        columns.called.push(leadData);
      } else if (leadData.verificationStatus === 'verified') {
        columns.verified.push(leadData);
      } else {
        columns.pending.push(leadData);
      }
    });
    return columns;
  }, [currentLeads]);
  const stats = useMemo(() => {
    const totalLeads = currentLeads.length;
    const collaborations = currentLeads.filter((l: any) => l.collaboration || l.callStatus === 'colaboreaza').length;
    const completedCalls = currentLeads.filter((l: any) => l.callCompleted).length;
    const progress = totalLeads > 0 ? completedCalls / totalLeads * 100 : 0;
    return {
      totalLeads,
      collaborations,
      progress: Math.round(progress),
      pending: kanbanColumns.pending.length,
      verified: kanbanColumns.verified.length,
      called: kanbanColumns.called.length,
      completed: kanbanColumns.completed.length
    };
  }, [currentLeads, kanbanColumns]);
  const handleLeadUpdate = (leadId: string, updates: any) => {
    updateProspectingLead(leadId, updates);
    if (selectedLead?.prospectId === leadId || selectedLead?.id === leadId) {
      setSelectedLead({
        ...selectedLead,
        ...updates
      });
    }
  };
  return <div className="w-full min-h-screen bg-[#F5F5F5]" data-id="element-10609">
      {/* Compact Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-10610">
        <div className="max-w-[1800px] mx-auto px-6 py-4" data-id="element-10611">
          <div className="flex items-center justify-between" data-id="element-10612">
            {/* Left: Title and Stats */}
            <div className="flex items-center space-x-6" data-id="element-10613">
              <div className="flex items-center space-x-3" data-id="element-10614">
                <FileTextIcon className="w-6 h-6 text-gray-400" data-id="element-10615" />
                <h1 className="text-xl font-bold text-gray-900" data-id="element-10616">
                  Kanban Prospectare
                </h1>
                <button onClick={() => navigate('/inchiriere/liste-prospectare')} className="flex items-center space-x-1 text-sm text-gray-600 hover:text-[#357BEB] transition-colors" data-id="element-10617">
                  <ListIcon className="w-4 h-4" data-id="element-10618" />
                  <span data-id="element-10619">List View</span>
                </button>
              </div>

              <div className="text-sm text-gray-600" data-id="element-10620">
                {stats.totalLeads} leaduri • {stats.collaborations} colaborări •{' '}
                {stats.progress}% progres
              </div>
            </div>

            {/* Right: Progress Bar and Search */}
            <div className="flex items-center space-x-4" data-id="element-10621">
              <div className="flex items-center space-x-2" data-id="element-10622">
                <span className="text-sm text-gray-600" data-id="element-10623">Progres zilnic</span>
                <div className="w-32 bg-gray-200 rounded-full h-2" data-id="element-10624">
                  <div className="bg-[#357BEB] h-2 rounded-full transition-all duration-500" style={{
                  width: `${stats.progress}%`
                }} data-id="element-10625" />
                </div>
                <span className="text-sm font-bold text-gray-900" data-id="element-10626">
                  {stats.progress}%
                </span>
              </div>

              <div className="relative" data-id="element-10627">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-10628" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Caută lead..." className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#357BEB] w-48" data-id="element-10629" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="max-w-[1800px] mx-auto px-6 py-6" data-id="element-10630">
        <div className="grid grid-cols-4 gap-4" data-id="element-10631">
          {/* Column 1: De Verificat */}
          <div className="bg-white rounded-lg shadow-sm" data-id="element-10632">
            <div className="bg-[#357BEB] text-white px-4 py-3 rounded-t-lg" data-id="element-10633">
              <div className="flex items-center justify-between" data-id="element-10634">
                <div className="flex items-center space-x-2" data-id="element-10635">
                  <SearchIcon className="w-4 h-4" data-id="element-10636" />
                  <div data-id="element-10637">
                    <h3 className="font-semibold text-sm" data-id="element-10638">De Verificat</h3>
                    <p className="text-xs opacity-80" data-id="element-10639">Verifică în CRM</p>
                  </div>
                </div>
                <span className="bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs font-bold" data-id="element-10640">
                  {stats.pending}
                </span>
              </div>
            </div>

            <div className="p-3 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto" data-id="element-10641">
              {kanbanColumns.pending.map((lead: any, index: number) => {
              const leadId = lead.id || lead.prospectId;
              return <motion.div key={leadId} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.03
              }} className="bg-white border border-gray-200 rounded-lg p-3 hover:border-[#357BEB] hover:shadow-sm transition-all cursor-pointer" onClick={() => setSelectedLead(lead)} data-id="element-10642">
                    <div className="flex items-center justify-between mb-2" data-id="element-10643">
                      <span className="text-xs font-mono text-gray-500" data-id="element-10644">
                        {leadId}
                      </span>
                    </div>

                    <h4 className="font-semibold text-gray-900 mb-2" data-id="element-10645">
                      {lead.zone}
                    </h4>

                    <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2" data-id="element-10646">
                      <span data-id="element-10647">{lead.rooms} cam</span>
                      <span data-id="element-10648">•</span>
                      <span data-id="element-10649">{lead.surface} mp</span>
                      <span data-id="element-10650">•</span>
                      <span className="font-semibold text-[#357BEB]" data-id="element-10651">
                        {lead.price}€
                      </span>
                    </div>

                    <div className="flex items-center space-x-1 text-xs text-gray-500 mb-3" data-id="element-10652">
                      <PhoneIcon className="w-3 h-3" data-id="element-10653" />
                      <span data-id="element-10654">{lead.phone}</span>
                    </div>

                    <button onClick={e => {
                  e.stopPropagation();
                  handleLeadUpdate(leadId, {
                    verificationStatus: 'verified',
                    timeline: [...(lead.timeline || []), {
                      action: 'Verificat în CRM - Nu există',
                      date: new Date().toLocaleTimeString('ro-RO', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    }]
                  });
                }} className="w-full bg-[#357BEB] text-white px-3 py-2 rounded text-xs font-semibold hover:bg-[#2868d8] transition-colors" data-id="element-10655">
                      ✓ Verificat - Continuă
                    </button>
                  </motion.div>;
            })}

              {kanbanColumns.pending.length === 0 && <div className="text-center py-12 text-gray-400" data-id="element-10656">
                  <CheckCircleIcon className="w-10 h-10 mx-auto mb-2 opacity-30" data-id="element-10657" />
                  <p className="text-sm" data-id="element-10658">Toate verificate!</p>
                </div>}
            </div>
          </div>

          {/* Column 2: De Apelat */}
          <div className="bg-white rounded-lg shadow-sm" data-id="element-10659">
            <div className="bg-[#9333EA] text-white px-4 py-3 rounded-t-lg" data-id="element-10660">
              <div className="flex items-center justify-between" data-id="element-10661">
                <div className="flex items-center space-x-2" data-id="element-10662">
                  <PhoneIcon className="w-4 h-4" data-id="element-10663" />
                  <div data-id="element-10664">
                    <h3 className="font-semibold text-sm" data-id="element-10665">De Apelat</h3>
                    <p className="text-xs opacity-80" data-id="element-10666">Efectuează apelul</p>
                  </div>
                </div>
                <span className="bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs font-bold" data-id="element-10667">
                  {stats.verified}
                </span>
              </div>
            </div>

            <div className="p-3 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto" data-id="element-10668">
              {kanbanColumns.verified.map((lead: any, index: number) => {
              const leadId = lead.id || lead.prospectId;
              return <motion.div key={leadId} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.03
              }} className="bg-white border border-gray-200 rounded-lg p-3 hover:border-[#9333EA] hover:shadow-sm transition-all cursor-pointer" onClick={() => setSelectedLead(lead)} data-id="element-10669">
                    <div className="flex items-center justify-between mb-2" data-id="element-10670">
                      <span className="text-xs font-mono text-gray-500" data-id="element-10671">
                        {leadId}
                      </span>
                    </div>

                    <h4 className="font-semibold text-gray-900 mb-2" data-id="element-10672">
                      {lead.zone}
                    </h4>

                    <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2" data-id="element-10673">
                      <span data-id="element-10674">{lead.rooms} cam</span>
                      <span data-id="element-10675">•</span>
                      <span data-id="element-10676">{lead.surface} mp</span>
                      <span data-id="element-10677">•</span>
                      <span className="font-semibold text-[#357BEB]" data-id="element-10678">
                        {lead.price}€
                      </span>
                    </div>

                    <a href={`tel:${lead.phone}`} onClick={e => e.stopPropagation()} className="flex items-center space-x-1 text-xs text-[#9333EA] font-semibold hover:underline mb-3" data-id="element-10679">
                      <PhoneIcon className="w-3 h-3" data-id="element-10680" />
                      <span data-id="element-10681">{lead.phone}</span>
                    </a>

                    <button onClick={e => {
                  e.stopPropagation();
                  handleLeadUpdate(leadId, {
                    callCompleted: true,
                    timeline: [...(lead.timeline || []), {
                      action: 'Apel efectuat',
                      date: new Date().toLocaleTimeString('ro-RO', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    }]
                  });
                }} className="w-full bg-[#9333EA] text-white px-3 py-2 rounded text-xs font-semibold hover:bg-[#7e22ce] transition-colors" data-id="element-10682">
                      ✓ Apel Finalizat
                    </button>
                  </motion.div>;
            })}

              {kanbanColumns.verified.length === 0 && <div className="text-center py-12 text-gray-400" data-id="element-10683">
                  <PhoneIcon className="w-10 h-10 mx-auto mb-2 opacity-30" data-id="element-10684" />
                  <p className="text-sm" data-id="element-10685">Niciun apel de făcut</p>
                </div>}
            </div>
          </div>

          {/* Column 3: Apelate */}
          <div className="bg-white rounded-lg shadow-sm" data-id="element-10686">
            <div className="bg-[#F59E0B] text-white px-4 py-3 rounded-t-lg" data-id="element-10687">
              <div className="flex items-center justify-between" data-id="element-10688">
                <div className="flex items-center space-x-2" data-id="element-10689">
                  <EditIcon className="w-4 h-4" data-id="element-10690" />
                  <div data-id="element-10691">
                    <h3 className="font-semibold text-sm" data-id="element-10692">Apelate</h3>
                    <p className="text-xs opacity-80" data-id="element-10693">Setează status</p>
                  </div>
                </div>
                <span className="bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs font-bold" data-id="element-10694">
                  {stats.called}
                </span>
              </div>
            </div>

            <div className="p-3 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto" data-id="element-10695">
              {kanbanColumns.called.map((lead: any, index: number) => {
              const leadId = lead.id || lead.prospectId;
              const invalid = isInvalidStatus(lead.callStatus);
              return <motion.div key={leadId} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.03
              }} className={`bg-white rounded-lg p-3 hover:shadow-sm transition-all cursor-pointer ${invalid ? 'border-2 border-red-500' : 'border border-gray-200 hover:border-[#F59E0B]'}`} onClick={() => setSelectedLead(lead)} data-id="element-10696">
                    <div className="flex items-center justify-between mb-2" data-id="element-10697">
                      <span className="text-xs font-mono text-gray-500" data-id="element-10698">
                        {leadId}
                      </span>
                      {invalid && <span className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold" data-id="element-10699">
                          ⛔
                        </span>}
                    </div>

                    <h4 className="font-semibold text-gray-900 mb-2" data-id="element-10700">
                      {lead.zone}
                    </h4>

                    <div className="flex items-center space-x-2 text-xs text-gray-600 mb-3" data-id="element-10701">
                      <span data-id="element-10702">{lead.rooms} cam</span>
                      <span data-id="element-10703">•</span>
                      <span data-id="element-10704">{lead.surface} mp</span>
                      <span data-id="element-10705">•</span>
                      <span className="font-semibold text-[#357BEB]" data-id="element-10706">
                        {lead.price}€
                      </span>
                    </div>

                    {invalid && <div className="bg-red-50 border border-red-200 rounded px-2 py-1 mb-2" data-id="element-10707">
                        <p className="text-xs text-red-600 font-semibold" data-id="element-10708">
                          ⛔ Lead invalid
                        </p>
                      </div>}

                    {lead.callStatus === 'colaboreaza' && !lead.propertyId && <input type="text" placeholder="P12399" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#21A366]" onClick={e => e.stopPropagation()} onKeyPress={e => {
                  if (e.key === 'Enter') {
                    const input = e.target as HTMLInputElement;
                    const propertyId = input.value.trim();
                    if (propertyId) {
                      handleLeadUpdate(leadId, {
                        propertyId: propertyId,
                        timeline: [...(lead.timeline || []), {
                          action: `ID Proprietate indexat: ${propertyId}`,
                          date: new Date().toLocaleTimeString('ro-RO', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        }]
                      });
                      input.value = '';
                    }
                  }
                }} data-id="element-10709" />}
                  </motion.div>;
            })}

              {kanbanColumns.called.length === 0 && <div className="text-center py-12 text-gray-400" data-id="element-10710">
                  <EditIcon className="w-10 h-10 mx-auto mb-2 opacity-30" data-id="element-10711" />
                  <p className="text-sm" data-id="element-10712">Niciun lead apelat</p>
                </div>}
            </div>
          </div>

          {/* Column 4: Finalizate */}
          <div className="bg-white rounded-lg shadow-sm" data-id="element-10713">
            <div className="bg-[#21A366] text-white px-4 py-3 rounded-t-lg" data-id="element-10714">
              <div className="flex items-center justify-between" data-id="element-10715">
                <div className="flex items-center space-x-2" data-id="element-10716">
                  <CheckCircleIcon className="w-4 h-4" data-id="element-10717" />
                  <div data-id="element-10718">
                    <h3 className="font-semibold text-sm" data-id="element-10719">Finalizate</h3>
                    <p className="text-xs opacity-80" data-id="element-10720">Colaborări</p>
                  </div>
                </div>
                <span className="bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-xs font-bold" data-id="element-10721">
                  {stats.completed}
                </span>
              </div>
            </div>

            <div className="p-3 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto" data-id="element-10722">
              {kanbanColumns.completed.map((lead: any, index: number) => {
              const leadId = lead.id || lead.prospectId;
              return <motion.div key={leadId} initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: index * 0.03
              }} className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-[#21A366] rounded-lg p-3 hover:shadow-sm transition-all cursor-pointer" onClick={() => setSelectedLead(lead)} data-id="element-10723">
                    <div className="flex items-center justify-between mb-2" data-id="element-10724">
                      <span className="text-xs font-mono text-[#21A366] font-semibold" data-id="element-10725">
                        {leadId}
                      </span>
                      <CheckCircleIcon className="w-4 h-4 text-[#21A366]" data-id="element-10726" />
                    </div>

                    <h4 className="font-semibold text-gray-900 mb-2" data-id="element-10727">
                      {lead.zone}
                    </h4>

                    <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2" data-id="element-10728">
                      <span data-id="element-10729">{lead.rooms} cam</span>
                      <span data-id="element-10730">•</span>
                      <span data-id="element-10731">{lead.surface} mp</span>
                      <span data-id="element-10732">•</span>
                      <span className="font-semibold text-[#357BEB]" data-id="element-10733">
                        {lead.price}€
                      </span>
                    </div>

                    {lead.propertyId && <div className="bg-white rounded px-2 py-1 mt-2" data-id="element-10734">
                        <p className="text-xs text-gray-500" data-id="element-10735">ID Proprietate</p>
                        <p className="text-sm font-bold text-[#21A366]" data-id="element-10736">
                          {lead.propertyId}
                        </p>
                      </div>}
                  </motion.div>;
            })}

              {kanbanColumns.completed.length === 0 && <div className="text-center py-12 text-gray-400" data-id="element-10737">
                  <CheckCircleIcon className="w-10 h-10 mx-auto mb-2 opacity-30" data-id="element-10738" />
                  <p className="text-sm" data-id="element-10739">Nicio colaborare</p>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      <AnimatePresence data-id="element-10740">
        {selectedLead && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSelectedLead(null)} className="fixed inset-0 bg-black bg-opacity-50 z-40" data-id="element-10741" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="fixed right-0 top-0 h-full w-full sm:w-[500px] bg-white shadow-2xl z-50 overflow-y-auto" data-id="element-10742">
              <div className="p-6" data-id="element-10743">
                <div className="flex items-center justify-between mb-6" data-id="element-10744">
                  <h3 className="text-xl font-bold text-[#132C53]" data-id="element-10745">
                    Detalii Lead
                  </h3>
                  <button onClick={() => setSelectedLead(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-10746">
                    <XIcon className="w-6 h-6 text-gray-500" data-id="element-10747" />
                  </button>
                </div>

                <div className="space-y-4" data-id="element-10748">
                  <div className={`rounded-xl p-6 text-white ${isInvalidStatus(selectedLead.callStatus) ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-[#357BEB] to-[#2868d8]'}`} data-id="element-10749">
                    <div className="flex items-center space-x-2 mb-3" data-id="element-10750">
                      <HashIcon className="w-5 h-5" data-id="element-10751" />
                      <span className="text-lg font-bold" data-id="element-10752">
                        {selectedLead.prospectId || selectedLead.id}
                      </span>
                      {isInvalidStatus(selectedLead.callStatus) && <span className="ml-2 px-2 py-0.5 bg-white bg-opacity-20 rounded-full text-xs font-bold" data-id="element-10753">
                          ⛔ INVALID
                        </span>}
                    </div>
                    <h4 className="text-2xl font-bold mb-2" data-id="element-10754">
                      {selectedLead.zone}
                    </h4>
                    <p className="text-3xl font-bold mb-3" data-id="element-10755">
                      {selectedLead.price} €/lună
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-10756">
                      <div data-id="element-10757">• {selectedLead.rooms} camere</div>
                      <div data-id="element-10758">• {selectedLead.surface} mp</div>
                      <div data-id="element-10759">• Etaj {selectedLead.floor}</div>
                      <div className="flex items-center space-x-1" data-id="element-10760">
                        <PhoneIcon className="w-3 h-3" data-id="element-10761" />
                        <span data-id="element-10762">{selectedLead.phone}</span>
                      </div>
                    </div>
                  </div>

                  {selectedLead.callCompleted && !isInvalidStatus(selectedLead.callStatus) && <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4" data-id="element-10763">
                        <div className="flex items-center space-x-2 mb-3" data-id="element-10764">
                          <EditIcon className="w-5 h-5 text-amber-600" data-id="element-10765" />
                          <h5 className="font-bold text-gray-900" data-id="element-10766">
                            Editează Status Apel
                          </h5>
                        </div>
                        <select value={selectedLead.callStatus} onChange={e => {
                  const newStatus = e.target.value;
                  const leadId = selectedLead.id || selectedLead.prospectId;
                  handleLeadUpdate(leadId, {
                    callStatus: newStatus,
                    collaboration: newStatus === 'colaboreaza',
                    timeline: [...(selectedLead.timeline || []), {
                      action: `Status actualizat: ${callStatusOptions.find(o => o.value === newStatus)?.label}`,
                      date: new Date().toLocaleTimeString('ro-RO', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    }]
                  });
                }} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB] font-medium bg-white text-sm" data-id="element-10767">
                          {callStatusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-10768">
                              {opt.label}
                            </option>)}
                        </select>
                        <p className="text-xs text-gray-600 mt-2" data-id="element-10769">
                          💡 Statusurile cu ⛔ opresc flow-ul de prospectare
                        </p>
                      </div>}

                  {isInvalidStatus(selectedLead.callStatus) && <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4" data-id="element-10770">
                      <div className="flex items-center space-x-3" data-id="element-10771">
                        <BanIcon className="w-8 h-8 text-red-500" data-id="element-10772" />
                        <div data-id="element-10773">
                          <p className="font-bold text-red-600" data-id="element-10774">
                            ⛔ Lead invalid - Flow oprit
                          </p>
                          <p className="text-sm text-gray-600" data-id="element-10775">
                            {callStatusOptions.find(o => o.value === selectedLead.callStatus)?.label}
                          </p>
                        </div>
                      </div>
                    </div>}

                  {selectedLead.callStatus === 'colaboreaza' && !selectedLead.propertyId && !isInvalidStatus(selectedLead.callStatus) && <div className="bg-green-50 border-2 border-[#21A366] rounded-lg p-4" data-id="element-10776">
                        <p className="text-sm font-semibold text-[#132C53] mb-3" data-id="element-10777">
                          ✅ Colaborare confirmată! Introdu ID-ul proprietății.
                        </p>
                        <input type="text" placeholder="P12399" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#21A366] font-medium mb-3" onKeyPress={e => {
                  if (e.key === 'Enter') {
                    const input = e.target as HTMLInputElement;
                    const propertyId = input.value.trim();
                    if (propertyId) {
                      const leadId = selectedLead.id || selectedLead.prospectId;
                      handleLeadUpdate(leadId, {
                        propertyId: propertyId,
                        timeline: [...(selectedLead.timeline || []), {
                          action: `ID Proprietate indexat: ${propertyId}`,
                          date: new Date().toLocaleTimeString('ro-RO', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        }]
                      });
                      input.value = '';
                    }
                  }
                }} data-id="element-10778" />
                      </div>}

                  {selectedLead.propertyId && <div className="bg-green-50 border-2 border-[#21A366] rounded-lg p-4" data-id="element-10779">
                      <div className="flex items-center space-x-3" data-id="element-10780">
                        <CheckCircleIcon className="w-8 h-8 text-[#21A366]" data-id="element-10781" />
                        <div data-id="element-10782">
                          <p className="font-bold text-[#21A366]" data-id="element-10783">
                            ✓ Colaborare finalizată!
                          </p>
                          <p className="text-sm text-gray-600" data-id="element-10784">
                            ID Proprietate:{' '}
                            <strong data-id="element-10785">{selectedLead.propertyId}</strong>
                          </p>
                        </div>
                      </div>
                    </div>}

                  {selectedLead.source1 && <div data-id="element-10786">
                      <h5 className="font-semibold text-gray-900 mb-3" data-id="element-10787">
                        Surse anunț
                      </h5>
                      <div className="space-y-2" data-id="element-10788">
                        <a href={selectedLead.source1} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-[#357BEB] hover:underline text-sm break-all" data-id="element-10789">
                          <ExternalLinkIcon className="w-4 h-4 flex-shrink-0" data-id="element-10790" />
                          <span data-id="element-10791">{selectedLead.source1}</span>
                        </a>
                      </div>
                    </div>}

                  <div data-id="element-10792">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10793">
                      Notițe agent
                    </label>
                    <textarea rows={4} value={selectedLead.notes || ''} onChange={e => {
                  const leadId = selectedLead.id || selectedLead.prospectId;
                  handleLeadUpdate(leadId, {
                    notes: e.target.value
                  });
                }} placeholder="Adaugă notițe despre proprietate sau apel..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" data-id="element-10794" />
                  </div>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}